/**
 * Lightweight markdown -> React renderer.
 *
 * Originally extracted from `pages/Docs.jsx` so the public Terms and Privacy
 * pages can render the same way without pulling in a full markdown library.
 *
 * Supports: headings (#, ##, ###), paragraphs, bullet lists, GitHub-style
 * tables, fenced code blocks, blockquotes, **bold**, `code`, [links](url).
 */

function inlineMd(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(
      /`([^`]+)`/g,
      '<code class="bg-muted/60 px-1 py-0.5 rounded text-[11px] font-mono">$1</code>',
    )
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" class="text-accent underline">$1</a>',
    );
}

export function renderMarkdown(src) {
  if (!src) return null;
  const lines = src.split("\n");
  const nodes = [];
  let inCode = false;
  let codeBuf = [];
  let listBuf = [];
  let tableBuf = [];

  const flushList = () => {
    if (listBuf.length) {
      nodes.push(
        <ul
          key={`ul-${nodes.length}`}
          className="list-disc ml-5 space-y-1 my-2 text-sm"
        >
          {listBuf.map((l, k) => (
            <li key={k} dangerouslySetInnerHTML={{ __html: inlineMd(l) }} />
          ))}
        </ul>,
      );
      listBuf = [];
    }
  };
  const flushTable = () => {
    if (tableBuf.length < 2) {
      tableBuf = [];
      return;
    }
    const [header, , ...rows] = tableBuf;
    const splitRow = (r) =>
      r
        .split("|")
        .slice(1, -1)
        .map((c) => c.trim());
    const headCells = splitRow(header);
    nodes.push(
      <div key={`tbl-${nodes.length}`} className="my-3 overflow-x-auto">
        <table className="w-full text-xs border border-border rounded-md">
          <thead className="bg-muted/50">
            <tr>
              {headCells.map((h, k) => (
                <th
                  key={k}
                  className="px-3 py-2 text-left font-medium border-b border-border"
                >
                  <span dangerouslySetInnerHTML={{ __html: inlineMd(h) }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, rk) => (
              <tr
                key={rk}
                className="border-b border-border/50 last:border-b-0"
              >
                {splitRow(r).map((c, ck) => (
                  <td key={ck} className="px-3 py-1.5">
                    <span dangerouslySetInnerHTML={{ __html: inlineMd(c) }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>,
    );
    tableBuf = [];
  };

  for (const line of lines) {
    if (line.startsWith("```")) {
      if (inCode) {
        nodes.push(
          <pre
            key={`code-${nodes.length}`}
            className="bg-muted/50 border border-border rounded-md p-3 my-3 text-xs overflow-x-auto font-mono whitespace-pre"
          >
            <code>{codeBuf.join("\n")}</code>
          </pre>,
        );
        codeBuf = [];
        inCode = false;
      } else {
        flushList();
        flushTable();
        inCode = true;
      }
      continue;
    }
    if (inCode) {
      codeBuf.push(line);
      continue;
    }
    if (line.startsWith("|") && line.lastIndexOf("|") > 0) {
      flushList();
      tableBuf.push(line);
      continue;
    }
    if (tableBuf.length && !line.startsWith("|")) flushTable();
    if (line.startsWith("# ")) {
      flushList();
      flushTable();
      nodes.push(
        <h1
          key={`h1-${nodes.length}`}
          className="text-2xl font-semibold mt-6 mb-3 text-foreground"
        >
          {line.slice(2)}
        </h1>,
      );
    } else if (line.startsWith("## ")) {
      flushList();
      flushTable();
      nodes.push(
        <h2
          key={`h2-${nodes.length}`}
          className="text-lg font-semibold mt-5 mb-2 text-foreground"
        >
          {line.slice(3)}
        </h2>,
      );
    } else if (line.startsWith("### ")) {
      flushList();
      flushTable();
      nodes.push(
        <h3
          key={`h3-${nodes.length}`}
          className="text-sm font-semibold mt-4 mb-1.5 text-foreground"
        >
          {line.slice(4)}
        </h3>,
      );
    } else if (line.startsWith("> ")) {
      flushList();
      nodes.push(
        <blockquote
          key={`q-${nodes.length}`}
          className="border-l-4 border-accent/40 pl-3 my-2 text-sm text-muted-foreground italic"
          dangerouslySetInnerHTML={{ __html: inlineMd(line.slice(2)) }}
        />,
      );
    } else if (/^[*-] /.test(line)) {
      listBuf.push(line.slice(2));
    } else if (line.trim() === "") {
      flushList();
    } else {
      flushList();
      nodes.push(
        <p
          key={`p-${nodes.length}`}
          className="text-sm my-2 leading-relaxed text-foreground/90"
          dangerouslySetInnerHTML={{ __html: inlineMd(line) }}
        />,
      );
    }
  }
  flushList();
  flushTable();
  return nodes;
}
